import type { TemplateOption } from "../hooks/useTemplateOptions";
import type { TemplateSuggestion } from "../service/schedulePick.service";
import { LC_KEYS } from "./constants";

/**
 * Why the one-click Start card names the template it does, best evidence first:
 * - "chosen": picked for this patient on their row (``rowChosen``), which beats everything, or
 *   picked in the card's footer (``chosen``), which is the default for rows that name nothing;
 * - "suggested": the server's template for the appointment's EHR visit type, applied only at or
 *   above the schedule's ``template_min_confidence`` (below it the row shows nothing and this chain
 *   carries on). It outranks the footer: the footer is a default for everyone, the suggestion is
 *   about this patient's visit, so changing the default must not silently change a row that names
 *   its template;
 * - "last_used": the template last picked on this device (what the drawer pre-fills);
 * - "recent_note": the template on the clinician's most recent note, for a new device, a
 *   cleared browser, or a re-login (signing out wipes local storage);
 * - "only_template": the clinician has exactly one template of their own to choose from.
 * With none of these the card starts anyway and the template is chosen when the recording
 * is submitted, through the same details drawer record-first uses.
 */
export type SessionTemplateSource = "chosen" | "suggested" | "last_used" | "recent_note" | "only_template";

/** The server's default bar for applying a visit-type suggestion, when the payload doesn't say. */
export const DEFAULT_TEMPLATE_MIN_CONFIDENCE = 0.7;

/** True when a suggestion is confident enough to be used as the template, not just shown. */
export const isSuggestionApplied = (
  suggestion: Pick<TemplateSuggestion, "confidence"> | null | undefined,
  minConfidence: number = DEFAULT_TEMPLATE_MIN_CONFIDENCE,
): boolean => suggestion != null && suggestion.confidence >= minConfidence;

export type SessionTemplate = {
  /** What goes into ``recordingDetail.note_type``. */
  value: number | string;
  name: string;
  /** Gallery template: the backend copies it into "My Templates" when the session starts. */
  isTemplate: boolean;
  templateId?: number;
  source: SessionTemplateSource;
  /** The visit-type suggestion behind a "suggested" template, for its events. */
  suggestion?: TemplateSuggestion;
};

/** ``template_suggestion_overridden`` properties: ids, labels and the score, never patient data. */
export const suggestionOverriddenProps = (
  surface: "card" | "drawer",
  suggestion: TemplateSuggestion,
  chosenNoteType: number | string | null | undefined,
  applied: boolean,
) => ({
  surface,
  visit_type: suggestion.visit_type,
  suggested_note_type: suggestion.note_type,
  chosen_note_type: chosenNoteType ?? null,
  confidence: suggestion.confidence,
  source: suggestion.source,
  applied,
});

/** The template last picked on this device, or null. Gallery picks are never stored. */
export const readStoredTemplateChoice = (): number | null => {
  if (localStorage.getItem(LC_KEYS.IS_TEMPLATE) === "true") return null;
  const raw = localStorage.getItem(LC_KEYS.NOTE_TYPE);
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * The visit details the drawer pre-fills from this device (CPT duration and location), read the
 * way the drawer reads them. A Quick Start with a template skips the drawer, so it carries these
 * itself; without them the note would be generated with fewer details than a drawer start.
 */
export const readStoredVisitDetails = (): { duration_type?: number | null; location_type?: number } => {
  const details: { duration_type?: number | null; location_type?: number } = {};
  const cpt = localStorage.getItem(LC_KEYS.CPT_CODE);
  if (cpt) details.duration_type = cpt === "undefined" ? null : Number(cpt);
  const location = localStorage.getItem(LC_KEYS.LOCATION_TYPE);
  if (location) details.location_type = Number(location);
  return details;
};

/** Remember a picker choice so the next drawer (and the Start card) pre-fills it. */
export const rememberTemplateChoice = (value: number | string, isGalleryTemplate: boolean): void => {
  if (!isGalleryTemplate) {
    localStorage.setItem(LC_KEYS.NOTE_TYPE, value.toString());
    localStorage.setItem(LC_KEYS.IS_TEMPLATE, "false");
  } else {
    localStorage.removeItem(LC_KEYS.NOTE_TYPE);
    localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
  }
};

const toSessionTemplate = (option: TemplateOption, source: SessionTemplateSource): SessionTemplate => ({
  value: option.value,
  name: option.searchText,
  isTemplate: Boolean(option.is_template),
  templateId: option.is_template ? option.id : undefined,
  source,
});

/**
 * The template a one-click start will use, or null when there is no evidence. Every
 * candidate is checked against the picker's current options, so a template that has
 * been deleted or unshared since it was last used (or suggested) is skipped rather than sent.
 */
export const resolveSessionTemplate = ({
  options,
  rowChosen,
  chosen,
  suggested,
  minConfidence = DEFAULT_TEMPLATE_MIN_CONFIDENCE,
  stored,
  recent,
}: {
  options: TemplateOption[];
  /** Picked on this patient's row: beats the suggestion and the footer. */
  rowChosen?: number | string | null;
  /** Picked in the card's footer: the default for rows that name nothing. */
  chosen?: number | string | null;
  /** The appointment's visit-type suggestion (``ScheduleSlot.template_suggestion``). */
  suggested?: TemplateSuggestion | null;
  /** ``SchedulePick.template_min_confidence``. */
  minConfidence?: number;
  stored?: number | null;
  recent?: number[] | null;
}): SessionTemplate | null => {
  const find = (value: number | string | null | undefined) =>
    value == null ? undefined : options.find((option) => option.value === value);

  const forRow = find(rowChosen);
  if (forRow) return toSessionTemplate(forRow, "chosen");

  // The gallery is a catalogue, not a choice the clinician made: never infer one from it.
  const own = (value: number | string | null | undefined) => {
    const option = find(value);
    return option && option.group !== "gallery" ? option : undefined;
  };

  if (suggested && isSuggestionApplied(suggested, minConfidence)) {
    const option = own(suggested.note_type);
    if (option) {
      return { ...toSessionTemplate(option, "suggested"), suggestion: suggested };
    }
  }

  const picked = find(chosen);
  if (picked) return toSessionTemplate(picked, "chosen");

  const last = own(stored);
  if (last) return toSessionTemplate(last, "last_used");

  for (const value of recent ?? []) {
    const option = own(value);
    if (option) return toSessionTemplate(option, "recent_note");
  }

  const mine = options.filter((option) => option.group !== "gallery");
  if (mine.length === 1) return toSessionTemplate(mine[0], "only_template");

  return null;
};

/**
 * What the Start card hands the recorder (``startQuickCapture``). With neither field it is
 * plain record-first: the sidebar's patient, no template.
 */
export type QuickCapturePreset = {
  /** Record for this patient instead of the sidebar's selection. */
  client?: { id: number; uuid?: string | null; name?: string | null } | null;
  /** Record with this template; null leaves it to the details drawer after Submit. */
  template?: SessionTemplate | null;
  /** "Record without a patient": ignore the sidebar selection too. */
  unassigned?: boolean;
  /** Properties for ``quick_start_started``, sent once the recording has really begun. */
  startedEvent?: Record<string, unknown>;
  /** Called when the start attempt is over, started or not. */
  onSettled?: () => void;
};
